<script setup>
import { computed } from 'vue';

const props = defineProps({
  articles: { type: Array, default: () => [] },
  // Разборы судебной практики (PracticeFeed): показываются первыми — они свежее и ближе к делу
  practice: { type: Array, default: () => [] },
});

const visible = computed(() => (props.articles || []).filter((a) => a && a.header && a.url));
const cases = computed(() => (props.practice || []).filter((p) => p && p.url && p.title));
const heading = computed(() => (cases.value.length ? 'Статьи и разборы практики' : 'Статьи по теме'));

const fmtDate = (d) => {
  if (!d) return '';
  try { return new Date(String(d).replace(' ', 'T')).toLocaleDateString('ru-RU', { day: 'numeric', month: 'long', year: 'numeric' }); }
  catch { return ''; }
};
</script>

<template>
  <section v-if="visible.length || cases.length" id="articles" class="bg-slate-50 scroll-mt-24">
    <div class="max-w-7xl mx-auto px-4 py-8 md:py-12">
      <div class="flex items-center gap-3 mb-6">
        <span class="flex-grow h-px bg-gray-200"></span>
        <h2 class="text-2xl lg:text-3xl font-extrabold tracking-tight text-gray-900 text-center">
          {{ heading }}
        </h2>
        <span class="flex-grow h-px bg-gray-200"></span>
      </div>

      <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        <!-- Разборы практики -->
        <a
          v-for="c in cases"
          :key="'case-' + c.id"
          :href="c.url"
          class="group flex flex-col h-full rounded-xl border bg-white p-5 shadow-sm hover:shadow-md transition-all"
          :class="c.kind === 'analysis' ? 'border-indigo-200 hover:border-indigo-300' : 'border-gray-100 hover:border-indigo-200'"
        >
          <span class="inline-flex w-max items-center gap-1 rounded-lg px-2 py-0.5 text-xs font-semibold"
            :class="c.kind === 'analysis' ? 'bg-indigo-50 text-indigo-700' : 'bg-gray-100 text-gray-600'">
            <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 3v18M5 7h14M7 7l-3 7a3 3 0 006 0L7 7zm10 0l-3 7a3 3 0 006 0l-3-7z" />
            </svg>
            {{ c.kind === 'analysis' ? 'Разбор юриста' : 'Решение суда' }}
          </span>
          <h3 class="mt-2 text-base md:text-lg font-bold leading-snug text-gray-900 line-clamp-3 group-hover:text-indigo-700 transition-colors">
            {{ c.title }}
          </h3>
          <p v-if="c.excerpt" class="mt-2 text-sm text-gray-500 leading-relaxed line-clamp-2 flex-grow">{{ c.excerpt }}</p>
          <p v-else class="mt-2 text-sm text-gray-500 flex-grow">{{ c.court }}, № {{ c.number }}</p>
          <p v-if="c.lawyers && c.lawyers.length" class="mt-2 text-xs text-gray-600">
            Разобрали: {{ c.lawyers.map((l) => l.name).join(', ') }}
          </p>
          <div class="mt-3 flex items-center justify-between gap-2 text-xs text-gray-500">
            <span>{{ c.actDate ? 'Решение от ' + c.actDate : '' }}</span>
            <span class="inline-flex items-center gap-1 text-indigo-600 font-medium group-hover:gap-1.5 transition-all">
              Читать
              <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
              </svg>
            </span>
          </div>
        </a>

        <!-- Статьи -->
        <a
          v-for="article in visible"
          :key="article.id"
          :href="route('articles/url', article.url)"
          class="group flex flex-col h-full rounded-xl border border-gray-100 bg-white p-5 shadow-sm hover:shadow-md hover:border-indigo-200 transition-all"
        >
          <h3 class="text-base md:text-lg font-bold leading-snug text-gray-900 line-clamp-4 group-hover:text-indigo-700 transition-colors">
            {{ article.header }}
          </h3>
          <p v-if="article.description" class="mt-2 text-sm text-gray-500 leading-relaxed line-clamp-2 flex-grow">
            {{ article.description }}
          </p>
          <div class="mt-3 flex items-center justify-between gap-2 text-xs text-gray-500">
            <span>{{ fmtDate(article.created_at) }}</span>
            <span class="inline-flex items-center gap-1 text-indigo-600 font-medium group-hover:gap-1.5 transition-all">
              Читать
              <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
              </svg>
            </span>
          </div>
        </a>
      </div>
    </div>
  </section>
</template>
